/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

'use client';

import { useEffect, useState, type ComponentProps } from 'react';
import { Toaster } from 'sonner';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

type ToasterPosition = NonNullable<ComponentProps<typeof Toaster>['position']>;
type ToasterOffset = Exclude<NonNullable<ComponentProps<typeof Toaster>['offset']>, string | number>;

interface ToasterThemeProps {
    position?: ToasterPosition;
}

const DESKTOP_VIEWPORT_OFFSET = '24px';
const MOBILE_VIEWPORT_OFFSET = '16px';

function createToasterOffset(viewportOffset: string): ToasterOffset {
    return {
        top: `max(var(--header-height, 0px), ${viewportOffset})`,
        right: viewportOffset,
        bottom: viewportOffset,
        left: viewportOffset,
    };
}

const toasterOffset = createToasterOffset(DESKTOP_VIEWPORT_OFFSET);
const mobileToasterOffset = createToasterOffset(MOBILE_VIEWPORT_OFFSET);

const toastClassNames = {
    toast: cn(
        'group/toast pointer-events-auto relative flex w-[388px] max-w-[calc(100vw-2rem)] items-center gap-4 overflow-hidden rounded-sm border border-[var(--toast-border)] bg-[var(--toast-bg)] py-6 pl-6 pr-8 text-[var(--toast-foreground)] shadow-[var(--toast-shadow)]',
        '[&_[data-content]]:flex [&_[data-content]]:min-w-0 [&_[data-content]]:flex-1 [&_[data-content]]:flex-col [&_[data-content]]:gap-1'
    ),
    title: 'text-sm leading-5 font-semibold text-[var(--toast-foreground)]',
    description: 'text-sm leading-5 font-normal text-[var(--toast-foreground)] opacity-90',
    icon: 'shrink-0 self-start pt-0.5 text-[var(--toast-icon)] [&>svg]:size-4',
    actionButton:
        'inline-flex h-8 shrink-0 items-center justify-center rounded-sm border border-transparent bg-[var(--toast-action-bg)] px-3 text-xs leading-4 font-semibold uppercase tracking-button text-[var(--toast-action-foreground)] transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus)] focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    cancelButton:
        'inline-flex h-8 shrink-0 items-center justify-center rounded-sm border border-[var(--toast-border)] bg-[var(--toast-bg)] px-3 text-xs leading-4 font-semibold uppercase tracking-button text-[var(--toast-foreground)] transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus)] focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    closeButton:
        'absolute top-1 right-1 inline-flex size-6 items-center justify-center rounded-sm border-0 bg-transparent p-1 text-foreground/50 shadow-none transition-colors hover:bg-transparent hover:text-[var(--toast-foreground)] focus-visible:bg-background focus-visible:text-[var(--toast-foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus)] focus-visible:ring-offset-2 focus-visible:ring-offset-background [&>svg]:size-4 [&>svg]:stroke-2',
};

/**
 * Client-side Toaster component that adapts to theme changes
 * Watches for changes to the 'dark' class on document.documentElement
 */
export function ToasterTheme({ position = 'top-center' }: ToasterThemeProps) {
    const [theme, setTheme] = useState<'light' | 'dark'>(() => {
        if (typeof window === 'undefined') {
            return 'light';
        }
        return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
    });

    useEffect(() => {
        // Watch for theme changes by observing class changes on documentElement
        const observer = new MutationObserver(() => {
            const isDark = document.documentElement.classList.contains('dark');
            setTheme(isDark ? 'dark' : 'light');
        });

        // Observe changes to the class attribute
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class'],
        });

        return () => {
            observer.disconnect();
        };
    }, []);

    return (
        <Toaster
            expand
            position={position}
            closeButton
            theme={theme}
            offset={toasterOffset}
            mobileOffset={mobileToasterOffset}
            icons={{ success: <Check className="size-4" strokeWidth={2.5} />, error: null, info: null, warning: null }}
            toastOptions={{ unstyled: true, classNames: toastClassNames }}
        />
    );
}
